import { type Static, Type } from "typebox";

import {
  MaintenanceDueStateName,
  MeterSource,
  VehicleLifecycleState,
} from "./enums.js";
import { defineRoute } from "./route.js";
import {
  errors,
  Exact,
  Extend,
  Identifier,
  ListOf,
  Nullable,
  StringEnum,
  Timestamp,
  WithTotal,
} from "./schema.js";

// --- Vehicles ----------------------------------------------------------------

/** What every view of a vehicle shows, from a list row to its own page. */
export const VehicleSummary = Exact({
  id: Identifier,
  internalLabel: Type.String(),
  lifecycleState: VehicleLifecycleState,
  make: Nullable(Type.String()),
  model: Nullable(Type.String()),
  modelYear: Nullable(Type.Integer()),
  plate: Nullable(Type.String()),
  plateJurisdiction: Nullable(Type.String()),
});
export type VehicleSummary = Static<typeof VehicleSummary>;

export const VehicleListItem = Extend(VehicleSummary, {
  currentDriverName: Nullable(Type.String()),
  currentOdometerMiles: Nullable(Type.Integer()),
  nextMaintenanceState: MaintenanceDueStateName,
  openIssueCount: Type.Integer(),
  /** Safety recalls that may apply and are not yet marked repaired. */
  openRecallCount: Type.Integer(),
});
export type VehicleListItem = Static<typeof VehicleListItem>;

export const VehicleList = WithTotal(VehicleListItem);
export type VehicleList = Static<typeof VehicleList>;

/** The facts kept about a vehicle beyond its summary. */
const VehicleFacts = Extend(VehicleSummary, {
  acquiredAt: Nullable(Timestamp),
  archivedAt: Nullable(Timestamp),
  soldAt: Nullable(Timestamp),
  /** Sent back on edit, so a stale form is refused rather than applied. */
  version: Type.Integer(),
  vin: Nullable(Type.String()),
});

/** A vehicle as its own page shows it. */
export const VehicleProfile = Extend(VehicleFacts, {
  currentOdometerMiles: Nullable(Type.Integer()),
});
export type VehicleProfile = Static<typeof VehicleProfile>;

/** A vehicle as an edit returns it: the stored record. */
export const VehicleRecord = Extend(VehicleFacts, {
  createdAt: Timestamp,
  organizationId: Identifier,
  updatedAt: Timestamp,
});
export type VehicleRecord = Static<typeof VehicleRecord>;

export const VehicleAssignmentHistoryItem = Exact({
  actorId: Nullable(Type.String()),
  actorName: Nullable(Type.String()),
  assignedAt: Timestamp,
  correctedAt: Nullable(Timestamp),
  driverId: Identifier,
  driverName: Type.String(),
  endedAt: Nullable(Timestamp),
  id: Identifier,
});
export type VehicleAssignmentHistoryItem = Static<
  typeof VehicleAssignmentHistoryItem
>;

export const VehicleMeterHistoryItem = Exact({
  id: Identifier,
  observedAt: Timestamp,
  odometerMiles: Type.Integer(),
  source: Type.String(),
});
export type VehicleMeterHistoryItem = Static<typeof VehicleMeterHistoryItem>;

/**
 * Sections the vehicle page reserves space for before they have data of
 * their own here: always empty, and fetched from their own routes.
 */
const ReservedTimeline = WithTotal(Type.Never());

export const VehicleDetail = Exact({
  timelines: Exact({
    assignments: WithTotal(VehicleAssignmentHistoryItem),
    documents: ReservedTimeline,
    issues: ReservedTimeline,
    meterEntries: WithTotal(VehicleMeterHistoryItem),
    services: ReservedTimeline,
  }),
  vehicle: VehicleProfile,
});
export type VehicleDetail = Static<typeof VehicleDetail>;

export const Created = Exact({ id: Identifier });
export type Created = Static<typeof Created>;

export const createVehicle = defineRoute({
  body: Exact({
    acquiredAt: Type.Optional(Type.String({ format: "date-time" })),
    initialOdometerMiles: Type.Optional(
      Type.Integer({ maximum: 10_000_000, minimum: 0 }),
    ),
    /** When the starting odometer was read. Now when omitted. */
    initialOdometerObservedAt: Type.Optional(Timestamp),
    internalLabel: Type.String({ minLength: 1 }),
    make: Type.Optional(Type.String({ maxLength: 120 })),
    model: Type.Optional(Type.String({ maxLength: 120 })),
    modelYear: Type.Optional(Type.Integer({ maximum: 3000, minimum: 1886 })),
    plate: Type.Optional(Type.String({ maxLength: 32 })),
    plateJurisdiction: Type.Optional(Type.String({ maxLength: 16 })),
    vin: Type.Optional(
      Type.String({ pattern: "^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$" }),
    ),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles",
  response: { 201: Created, ...errors(400, 401, 403, 409, 422) },
  tags: ["vehicles"],
});

/** A vehicle a new one might duplicate, for the review before saving. */
export const PossibleVehicleMatch = Exact({
  archivedAt: Nullable(Timestamp),
  id: Identifier,
  internalLabel: Type.String(),
  lifecycleState: VehicleLifecycleState,
  /**
   * What it shares with the new one. "plate_and_jurisdiction" and "vin"
   * cannot be saved twice; a label cannot either when it matches exactly.
   */
  matchedOn: Type.Array(
    StringEnum(["label", "plate", "plate_and_jurisdiction", "vin"]),
  ),
  plate: Nullable(Type.String()),
  plateJurisdiction: Nullable(Type.String()),
  soldAt: Nullable(Timestamp),
  vin: Nullable(Type.String()),
});
export type PossibleVehicleMatch = Static<typeof PossibleVehicleMatch>;

export const PossibleVehicleMatches = ListOf(PossibleVehicleMatch);
export type PossibleVehicleMatches = Static<typeof PossibleVehicleMatches>;

// Write permission, not read: it exists to help someone adding a vehicle,
// and answers only within the caller's own organization.
export const findPossibleVehicleMatches = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/possible-matches",
  querystring: Exact({
    jurisdiction: Type.Optional(Type.String({ maxLength: 16 })),
    label: Type.Optional(Type.String({ maxLength: 120 })),
    plate: Type.Optional(Type.String({ maxLength: 32 })),
    vin: Type.Optional(Type.String({ maxLength: 64 })),
  }),
  response: { 200: PossibleVehicleMatches, ...errors(401, 403) },
  tags: ["vehicles"],
});

export const listVehicles = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles",
  querystring: Exact({
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
    pageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
    search: Type.Optional(Type.String({ maxLength: 120 })),
    status: Type.Optional(VehicleLifecycleState),
  }),
  response: { 200: VehicleList, ...errors(401, 403) },
  tags: ["vehicles"],
});

export const getVehicle = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId",
  querystring: Exact({
    meterOffset: Type.Optional(Type.Integer({ minimum: 0 })),
    meterPageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
  }),
  response: { 200: VehicleDetail, ...errors(401, 403, 404) },
  tags: ["vehicles"],
});

export const updateVehicle = defineRoute({
  body: Exact({
    acquiredAt: Type.Optional(Nullable(Type.String({ format: "date-time" }))),
    internalLabel: Type.Optional(Type.String({ minLength: 1 })),
    make: Type.Optional(Nullable(Type.String({ maxLength: 120 }))),
    model: Type.Optional(Nullable(Type.String({ maxLength: 120 }))),
    modelYear: Type.Optional(
      Nullable(Type.Integer({ maximum: 3000, minimum: 1886 })),
    ),
    plate: Type.Optional(Nullable(Type.String({ maxLength: 32 }))),
    plateJurisdiction: Type.Optional(Nullable(Type.String({ maxLength: 16 }))),
    version: Type.Integer({ minimum: 1 }),
    vin: Type.Optional(
      Nullable(Type.String({ pattern: "^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$" })),
    ),
  }),
  method: "PATCH",
  path: "/organizations/:organizationId/vehicles/:vehicleId",
  response: { 200: VehicleRecord, ...errors(400, 401, 403, 409) },
  tags: ["vehicles"],
});

export const changeVehicleLifecycle = defineRoute({
  body: Exact({
    lifecycleState: VehicleLifecycleState,
    soldAt: Type.Optional(Type.String({ format: "date-time" })),
    version: Type.Integer({ minimum: 1 }),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/lifecycle",
  response: { 200: VehicleRecord, ...errors(400, 401, 403, 409) },
  tags: ["vehicles"],
});

const DecodedVehicle = Exact({
  make: Type.Optional(Type.String()),
  model: Type.Optional(Type.String()),
  modelYear: Type.Optional(Type.Integer()),
  vin: Type.String(),
});

/**
 * A VIN lookup never fails the form: an unreadable or unavailable result
 * says so, and the person types the details in.
 */
export const VinDecodeResult = Type.Union([
  Exact({ kind: Type.Literal("decoded"), vehicle: DecodedVehicle }),
  Exact({ kind: Type.Literal("incomplete"), vehicle: DecodedVehicle }),
  Exact({
    kind: Type.Literal("invalid_vin"),
    manualEntryAllowed: Type.Literal(true),
    vin: Type.String(),
  }),
  Exact({
    kind: Type.Literal("temporarily_unavailable"),
    manualEntryAllowed: Type.Literal(true),
    vin: Type.String(),
  }),
]);
export type VinDecodeResult = Static<typeof VinDecodeResult>;

export const decodeVin = defineRoute({
  body: Exact({ vin: Type.String({ maxLength: 64 }) }),
  method: "POST",
  path: "/vehicles/decode-vin",
  response: { 200: VinDecodeResult, ...errors(400, 401, 403) },
  tags: ["vehicles"],
});

// --- Assignments --------------------------------------------------------------

export const VehicleAssignment = Exact({
  assignedAt: Timestamp,
  assignedByUserId: Nullable(Type.String()),
  correctedAt: Nullable(Timestamp),
  correctsAssignmentId: Nullable(Type.String()),
  createdAt: Timestamp,
  driverId: Identifier,
  endedAt: Nullable(Timestamp),
  id: Identifier,
  notes: Nullable(Type.String()),
  organizationId: Identifier,
  updatedAt: Timestamp,
  vehicleId: Identifier,
});
export type VehicleAssignment = Static<typeof VehicleAssignment>;

const AssignBody = Exact({
  driverId: Identifier,
  effectiveAt: Type.String({ format: "date-time" }),
});

const assignmentErrors = errors(400, 401, 403, 404, 409, 422);

export const assignVehicle = defineRoute({
  body: AssignBody,
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/assignments",
  response: { 201: VehicleAssignment, ...assignmentErrors },
  tags: ["vehicle assignments"],
});

export const reassignVehicle = defineRoute({
  body: AssignBody,
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/assignments/reassign",
  response: { 200: VehicleAssignment, ...assignmentErrors },
  tags: ["vehicle assignments"],
});

export const endVehicleAssignment = defineRoute({
  body: Exact({ effectiveAt: Type.String({ format: "date-time" }) }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/assignments/:assignmentId/end",
  response: { 200: VehicleAssignment, ...assignmentErrors },
  tags: ["vehicle assignments"],
});

export const correctVehicleAssignment = defineRoute({
  body: Exact({
    ...AssignBody.properties,
    endedAt: Type.Optional(Type.String({ format: "date-time" })),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/assignments/:assignmentId/correct",
  response: { 200: VehicleAssignment, ...assignmentErrors },
  tags: ["vehicle assignments"],
});

// --- Mileage --------------------------------------------------------------

export const MeterEntry = Exact({
  correctsEntryId: Nullable(Type.String()),
  id: Identifier,
  invalidatedAt: Nullable(Type.String()),
  observedAt: Type.String(),
  odometerMiles: Type.Integer(),
  source: MeterSource,
});
export type MeterEntry = Static<typeof MeterEntry>;

/** A reading in the history, with who entered it. */
export const MeterEntryListItem = Extend(MeterEntry, {
  /** The member or driver who entered it; null for one from before names were kept. */
  recordedBy: Nullable(Type.String()),
});
export type MeterEntryListItem = Static<typeof MeterEntryListItem>;

export const MeterEntryList = WithTotal(MeterEntryListItem);
export type MeterEntryList = Static<typeof MeterEntryList>;

const MeterReading = Exact({
  observedAt: Type.String({ format: "date-time" }),
  // The upper bound matches the database check constraint, so an absurd
  // value is refused by validation rather than by a 500 from Postgres.
  odometerMiles: Type.Integer({ maximum: 10_000_000, minimum: 0 }),
});

export const listMeterEntries = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/meter-entries",
  querystring: Exact({
    includeInvalidated: Type.Optional(Type.Boolean()),
    page: Type.Optional(Type.Integer({ minimum: 1 })),
    pageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
  }),
  response: { 200: MeterEntryList, ...errors(401, 403) },
  tags: ["mileage"],
});

/** A reading as the odometer rule judges it: a value, and when it was seen. */
export const OdometerReading = Exact({
  observedAt: Type.Optional(Type.String()),
  odometerMiles: Type.Integer(),
});
export type OdometerReading = Static<typeof OdometerReading>;

/** The readings a new one at an instant has to fit between. */
export const OdometerBounds = Exact({
  /** The earliest valid reading after it. */
  next: Nullable(OdometerReading),
  /** The latest valid reading at or before it. */
  previous: Nullable(OdometerReading),
});
export type OdometerBounds = Static<typeof OdometerBounds>;

// So a form can apply the same rule the server will before anything is
// submitted, for any date.
export const getOdometerBounds = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vehicles/:vehicleId/meter-entries/bounds",
  querystring: Exact({
    at: Type.String({ format: "date-time" }),
    // Correcting something sets its reading aside, so the form correcting it
    // judges the new value without it, as the server will.
    excludeEntryId: Type.Optional(Identifier),
    replacingServiceRecordId: Type.Optional(Identifier),
  }),
  response: { 200: OdometerBounds, ...errors(401, 403) },
  tags: ["mileage"],
});

export const recordMeterEntry = defineRoute({
  body: MeterReading,
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/meter-entries",
  response: { 201: MeterEntry, ...errors(401, 403, 409, 422) },
  tags: ["mileage"],
});

export const correctMeterEntry = defineRoute({
  body: Exact({
    ...MeterReading.properties,
    reason: Type.Optional(Type.String({ maxLength: 500 })),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vehicles/:vehicleId/meter-entries/:entryId/correct",
  response: { 200: MeterEntry, ...errors(401, 403, 404, 409, 422) },
  tags: ["mileage"],
});
